import React, { useEffect, useState } from 'react';
import { Button, Input, InputNumber, message, Switch, Typography, Upload } from 'antd';
import './detail-index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import { useNavigate, useParams } from 'react-router-dom';
import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { Editor } from '@tinymce/tinymce-react';
import {
  createLifehack,
  findLifehackByCondition,
  resetDetail,
  updateLifehack,
} from '../../../../store/lifehack/lifehackSlice';

const { Text } = Typography;
const LifehackDetail: React.FC = () => {
  const lifehackSlice = useSelector((state: RootState) => state.lifehack);
  const { detail } = lifehackSlice;
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState<number>(1);
  const [keyword, setKeyword] = useState('');
  const [define, setDefine] = useState('');
  const [description, setDescription] = useState('');
  const [previewImage, setPreviewImage] = useState<any>('');
  const [imageUrl, setImageUrl] = useState('');
  const [visibility, setVisibility] = useState(true);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      dispatch(findLifehackByCondition({ id: id }));
    }
    return () => {
      dispatch(resetDetail());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (detail && id) {
      setPage(detail.page);
      setKeyword(detail.keyword);
      setDefine(detail.define);
      setDescription(detail.description);
      setVisibility(detail.visibility === 'true' ? true : false);
      setPreviewImage(detail.image ? `${process.env.REACT_APP_API_URL}${detail.image}` : '');
    }
  }, [id, detail]);

  const handleSave = async () => {
    if (!keyword.trim()) {
      message.error('タイトルを入力してください。');
      return;
    }
    if (!id && !imageUrl) {
      message.error('画像を選択してください。');
      return;
    }
    if (id && !previewImage && !imageUrl) {
      message.error('画像を選択してください。');
      return;
    }
    if (!define.trim()) {
      message.error('説明を入力してください。');
      return;
    }
    if (!description || description === '<p></p>' || description.trim() === '') {
      message.error('詳細を入力してください。');
      return;
    }
    const formData = new FormData();
    formData.append('page', page.toString());
    formData.append('keyword', keyword);
    formData.append('define', define);
    formData.append('description', description);
    if (imageUrl) {
      formData.append('image', imageUrl);
    }
    formData.append('visibility', visibility.toString());
    if (id) {
      formData.append('id', id);
      const result = await dispatch(updateLifehack(formData));
      if (result.meta.requestStatus === 'fulfilled') {
        message.success('更新が成功しました!');
        navigate('/life-hack');
      } else {
        message.error('更新に失敗しました。もう一度お試しください。');
      }
    } else {
      const result = await dispatch(createLifehack(formData));
      if (result.meta.requestStatus === 'fulfilled') {
        message.success('新規作成が成功しました!');
        navigate('/life-hack');
      } else {
        message.error('新規作成に失敗しました。もう一度お試しください!');
      }
    }
  };
  const handleBack = () => {
    window.history.back();
  };

  const handleUploadChange = async ({ fileList: newFileList }) => {
    const latestFile = newFileList[newFileList.length - 1];
    if (latestFile && latestFile.originFileObj) {
      const objectUrl = URL.createObjectURL(latestFile.originFileObj);
      setPreviewImage(objectUrl);
      setImageUrl(latestFile.originFileObj);
    }
  };

  const resetForm = () => {
    setPage(1);
    setKeyword('');
    setDefine('');
    setDescription('');
    setPreviewImage('');
    setImageUrl('');
    setVisibility(false);
  };

  return (
    <div className="voucher-detail">
      <div className="header">
        <Button className="back-button" onClick={handleBack}>
          ←
        </Button>
        <Button className="save-button" type="primary" onClick={handleSave}>
          保存
        </Button>
      </div>
      <div className="section-title">
        投稿情報
        <Button
          type="text"
          icon={
            <img src={`${process.env.PUBLIC_URL}/assets/icons/delete.png`} alt="Delete Logo" className="icon-table" />
          }
          danger
          onClick={() => resetForm()}
        />
      </div>
      <div className="form-container">
        <div className="form-group">
          <label>Page</label>
          <InputNumber min={1} value={page} onChange={(e) => setPage(e as number)} />
        </div>
        <div className="form-group">
          <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
            <label className="switch-label" style={{ marginRight: '8px' }}>
              プライバシー
            </label>
            <Switch checked={visibility} onChange={() => setVisibility(!visibility)} />
            <label className="switch-label" style={{ marginLeft: '8px' }}>
              出版
            </label>
          </div>
        </div>
        <div className="form-group">
          <label>タイトル</label>
          <Input placeholder="コンテンツを入力" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
        </div>
        <div className="form-group">
          <label>画像表示</label>
          <div className="image-upload-container">
            <div className="image-preview-wrapper">
              {previewImage ? (
                <>
                  <img src={previewImage} alt="uploaded" className="preview-img" />
                  <div className="image-upload-overlay">
                    <Upload
                      accept=".png,.jpg,.jpeg,.gif,.bmp,.webp,.svg"
                      name="image"
                      listType="picture"
                      maxCount={1}
                      onChange={handleUploadChange}
                      showUploadList={false}
                      beforeUpload={(file) => {
                        const isImage = file.type.startsWith('image/');
                        if (!isImage) {
                          message.error('画像ファイルのみアップロード可能です。');
                        }
                        const isLt2M = file.size / 1024 / 1024 < 2;
                        if (!isLt2M) {
                          message.error('画像サイズは2MB以下である必要があります。');
                        }
                        return false;
                      }}
                    >
                      <Button
                        className="upload-button overlay-button"
                        icon={<UploadOutlined className="upload-icon" />}
                      >
                        アップロード
                      </Button>
                    </Upload>
                  </div>
                </>
              ) : (
                <Upload
                  name="image"
                  listType="picture"
                  maxCount={1}
                  onChange={handleUploadChange}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      message.error('画像ファイルのみアップロード可能です。');
                    }
                    const isLt2M = file.size / 1024 / 1024 < 2;
                    if (!isLt2M) {
                      message.error('画像サイズは2MB以下である必要があります。');
                    }
                    return false;
                  }}
                >
                  <Button className="upload-button" icon={<UploadOutlined className="upload-icon" />}>
                    アップロード
                  </Button>
                </Upload>
              )}
            </div>
            <div className="info-text-container">
              <InfoCircleOutlined className="info-text-icon" />
              <Text className="info-text" type="secondary">
                最適に表示するために、サイズ（300×600px）の画像を選択してください
              </Text>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label>説明</label>
          <Input.TextArea placeholder="内容を入力" value={define} onChange={(e) => setDefine(e.target.value)} />
        </div>
        <div className="form-group">
          <label>詳細</label>
          <Editor
            apiKey="no4n0lg2jp2odmg7erqlqdzwqytanzlnxyranmtd105te12y"
            value={description}
            init={{
              height: 300,
              plugins: ['lists'],
              statusbar: false,
              menubar: false,
              content_style: `body {
               font-family: 'Noto Sans JP', sans-serif;  
               font-size: 16px;                 
               line-height: 1.5;
            }`,
              toolbar:
                'undo redo | formatselect | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | forecolor | removeformat',
            }}
            onEditorChange={(content) => {
              setDescription(content);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default LifehackDetail;
